import { useEffect, useRef, useCallback } from "react";
import { useKLineData } from "@/hooks/useKLineData";

/**
 * KLineCanvas — BTC 实时 K 线分时背景动画
 */

interface KLineCanvasProps {
  height?: number;
  className?: string;
  opacity?: number;
}

const DOT_X_RATIO = 0.5;
const VISIBLE_LEFT_POINTS = 100;
const SLIDE_SPEED = 0.028;
/** 圆点纵向跟随目标价，越大越跟手（过大易抖） */
const DOT_Y_LERP = 0.14;

// 以改变可见位置：调下面两个常数即可整体上/下移 K 线（数值越小越靠上，越大纵向幅度）
const PRICE_BAND_CENTER_Y = 0.34;
const PRICE_BAND_HEIGHT_RATIO = 0.5;

export default function KLineCanvas({
  height = 400,
  className = "",
  opacity = 0.45,
}: KLineCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animRef = useRef<number>(0);
  const breathRef = useRef(0);
  const smoothDotYRef = useRef<number | null>(null);

  const klineData = useKLineData();

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const w = canvas.width / dpr;
    const h = canvas.height / dpr;
    const prices = klineData.pricesRef.current;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (prices.length < 2) {
      animRef.current = requestAnimationFrame(draw);
      return;
    }

    if (klineData.offsetRef.current > 0) {
      klineData.offsetRef.current = Math.max(
        0,
        klineData.offsetRef.current - SLIDE_SPEED
      );
    }
    const slideProgress = klineData.offsetRef.current;

    const dotFixedX = w * DOT_X_RATIO;

    const totalPrices = prices.length;
    const leftCount = Math.min(VISIBLE_LEFT_POINTS, totalPrices - 1);
    const startIdx = Math.max(0, totalPrices - leftCount - 1);
    const visiblePrices = prices.slice(startIdx);

    const minP = Math.min(...visiblePrices);
    const maxP = Math.max(...visiblePrices);
    const basePrice = klineData.basePriceRef.current;
    /**
     * 纵轴「最小可视跨度」：原为 basePrice*0.004（约 0.4%），小波动在屏上几乎成一条直线。
     * 改为约 0.03% 量级，让常见盘口抖动在背景上更明显（仍保留极小下限避免除零）。
     */
    const range = Math.max(maxP - minP, basePrice * 0.0003, minP * 1e-6 || 1);
    const midP = (minP + maxP) / 2;

    const priceToY = (p: number) => {
      const ratio = (p - midP) / range;
      return h * PRICE_BAND_CENTER_Y - ratio * h * PRICE_BAND_HEIGHT_RATIO;
    };

    const stepX = leftCount > 0 ? dotFixedX / leftCount : 1;
    const slideOffsetX = slideProgress * stepX;

    const getX = (i: number) => {
      const distFromEnd = visiblePrices.length - 1 - i;
      return dotFixedX - distFromEnd * stepX - slideOffsetX;
    };

    const currentPrice = visiblePrices[visiblePrices.length - 1];
    const targetDotY = priceToY(currentPrice);

    if (smoothDotYRef.current === null) {
      smoothDotYRef.current = targetDotY;
    } else {
      smoothDotYRef.current +=
        (targetDotY - smoothDotYRef.current) * DOT_Y_LERP;
    }
    const dotY = smoothDotYRef.current;

    const gradient = ctx.createLinearGradient(0, 0, 0, h);
    gradient.addColorStop(0, "rgba(59,130,246,0.04)");
    gradient.addColorStop(0.4, "rgba(59,130,246,0.015)");
    gradient.addColorStop(1, "rgba(59,130,246,0)");

    ctx.beginPath();
    let firstDrawn = false;
    let firstX = 0;
    for (let i = 0; i < visiblePrices.length; i++) {
      const x = getX(i);
      if (x < -10) continue;
      if (x > dotFixedX + 2) break;
      const y = priceToY(visiblePrices[i]);
      if (!firstDrawn) {
        ctx.moveTo(x, y);
        firstX = x;
        firstDrawn = true;
      } else {
        const prevX = getX(i - 1);
        const prevY = priceToY(visiblePrices[i - 1]);
        const cpx = (prevX + x) / 2;
        ctx.bezierCurveTo(cpx, prevY, cpx, y, x, y);
      }
    }
    if (firstDrawn) {
      const endX = Math.min(getX(visiblePrices.length - 1), dotFixedX);
      ctx.lineTo(endX, h);
      ctx.lineTo(firstX, h);
      ctx.closePath();
      ctx.fillStyle = gradient;
      ctx.fill();
    }

    ctx.beginPath();
    firstDrawn = false;
    for (let i = 0; i < visiblePrices.length; i++) {
      const x = getX(i);
      if (x < -10) continue;
      if (x > dotFixedX + 2) break;
      const y = priceToY(visiblePrices[i]);
      if (!firstDrawn) {
        ctx.moveTo(x, y);
        firstDrawn = true;
      } else {
        const prevX = getX(i - 1);
        const prevY = priceToY(visiblePrices[i - 1]);
        const cpx = (prevX + x) / 2;
        ctx.bezierCurveTo(cpx, prevY, cpx, y, x, y);
      }
    }
    ctx.strokeStyle = "rgba(59,130,246,0.5)";
    ctx.lineWidth = 1.35;
    ctx.stroke();

    const prevPrice =
      visiblePrices.length > 1
        ? visiblePrices[visiblePrices.length - 2]
        : currentPrice;
    const isUp = currentPrice >= prevPrice;
    const dotColor = isUp ? "34,197,94" : "239,68,68";

    breathRef.current += 0.03;
    const breathScale = 1 + 0.3 * Math.sin(breathRef.current);
    const coreRadius = 3.5 * breathScale;
    const glowRadius = 20 * breathScale;
    const glowOpacity = 0.25 + 0.1 * Math.sin(breathRef.current);

    const glow = ctx.createRadialGradient(
      dotFixedX,
      dotY,
      0,
      dotFixedX,
      dotY,
      glowRadius
    );
    glow.addColorStop(0, `rgba(${dotColor},${glowOpacity})`);
    glow.addColorStop(1, `rgba(${dotColor},0)`);
    ctx.beginPath();
    ctx.arc(dotFixedX, dotY, glowRadius, 0, Math.PI * 2);
    ctx.fillStyle = glow;
    ctx.fill();

    ctx.beginPath();
    ctx.arc(dotFixedX, dotY, coreRadius, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(${dotColor},0.9)`;
    ctx.fill();

    const ripples = klineData.ripplesRef.current;
    for (let i = ripples.length - 1; i >= 0; i--) {
      const r = ripples[i];
      r.x = dotFixedX;
      r.y = dotY;
      ctx.beginPath();
      ctx.arc(r.x, r.y, r.radius, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(${r.color},${r.opacity})`;
      ctx.lineWidth = 1.2;
      ctx.stroke();
      r.radius += 0.5;
      r.opacity -= 0.008;
      if (r.opacity <= 0 || r.radius >= r.maxRadius) {
        ripples.splice(i, 1);
      }
    }

    /** 最新价与 24h% 均来自 btcusdt@ticker（c / P） */
    const displayPrice = currentPrice;
    const change24h = klineData.ticker24hPctRef.current;
    const labelIsUp = change24h >= 0;
    const labelColor = labelIsUp ? "34,197,94" : "239,68,68";
    const arrow = labelIsUp ? "▲" : "▼";
    const changeStr = `${arrow} ${Math.abs(change24h).toFixed(2)}%`;
    const priceStr = `BTC $${displayPrice.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

    const fontSize = 11;
    const smallFontSize = 9;
    ctx.save();

    ctx.font = `600 ${fontSize}px -apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif`;
    const priceTextW = ctx.measureText(priceStr).width;
    ctx.font = `600 ${smallFontSize}px -apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif`;
    const changeTextW = ctx.measureText(changeStr).width;

    const pillPadX = 8;
    const pillPadY = 5;
    const gap = 6;
    const sparklineW = 36;
    const sparklineGap = 6;
    const contentW = priceTextW + gap + changeTextW + sparklineGap + sparklineW;
    const pillW = contentW + pillPadX * 2;
    const pillH = fontSize + pillPadY * 2;
    const pillX = dotFixedX + 16;
    const pillY = dotY - pillH / 2;

    ctx.beginPath();
    const pillR = pillH / 2;
    ctx.moveTo(pillX + pillR, pillY);
    ctx.lineTo(pillX + pillW - pillR, pillY);
    ctx.arcTo(pillX + pillW, pillY, pillX + pillW, pillY + pillR, pillR);
    ctx.arcTo(
      pillX + pillW,
      pillY + pillH,
      pillX + pillW - pillR,
      pillY + pillH,
      pillR
    );
    ctx.lineTo(pillX + pillR, pillY + pillH);
    ctx.arcTo(pillX, pillY + pillH, pillX, pillY + pillH - pillR, pillR);
    ctx.arcTo(pillX, pillY, pillX + pillR, pillY, pillR);
    ctx.closePath();
    ctx.fillStyle = `rgba(${labelColor},0.1)`;
    ctx.fill();
    ctx.strokeStyle = `rgba(${labelColor},0.25)`;
    ctx.lineWidth = 0.6;
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(dotFixedX + 6, dotY);
    ctx.lineTo(pillX, dotY);
    ctx.strokeStyle = `rgba(${labelColor},0.2)`;
    ctx.lineWidth = 0.8;
    ctx.setLineDash([2, 3]);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.font = `600 ${fontSize}px -apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif`;
    ctx.fillStyle = `rgba(${labelColor},0.75)`;
    ctx.fillText(priceStr, pillX + pillPadX, pillY + pillH / 2);

    ctx.font = `700 ${smallFontSize}px -apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif`;
    ctx.fillStyle = `rgba(${labelColor},0.9)`;
    ctx.fillText(
      changeStr,
      pillX + pillPadX + priceTextW + gap,
      pillY + pillH / 2
    );

    const sparkX =
      pillX + pillPadX + priceTextW + gap + changeTextW + sparklineGap;
    const sparkY = pillY + 3;
    const sparkH = pillH - 6;
    const sparkData = prices.slice(-30);
    if (sparkData.length > 2) {
      const sMin = Math.min(...sparkData);
      const sMax = Math.max(...sparkData);
      const sMid = (sMin + sMax) / 2;
      const sRange = Math.max(sMax - sMin, sMid * 0.00025, 0.01);
      const sStepX = sparklineW / (sparkData.length - 1);

      ctx.beginPath();
      for (let i = 0; i < sparkData.length; i++) {
        const sx = sparkX + i * sStepX;
        const sy = sparkY + sparkH - ((sparkData[i] - sMin) / sRange) * sparkH;
        if (i === 0) {
          ctx.moveTo(sx, sy);
        } else {
          const prevSx = sparkX + (i - 1) * sStepX;
          const prevSy =
            sparkY + sparkH - ((sparkData[i - 1] - sMin) / sRange) * sparkH;
          const cpx = (prevSx + sx) / 2;
          ctx.bezierCurveTo(cpx, prevSy, cpx, sy, sx, sy);
        }
      }
      ctx.strokeStyle = `rgba(${labelColor},0.6)`;
      ctx.lineWidth = 0.8;
      ctx.stroke();
    }

    ctx.restore();

    const midY = h * PRICE_BAND_CENTER_Y;
    ctx.setLineDash([4, 8]);
    ctx.beginPath();
    ctx.moveTo(0, midY);
    ctx.lineTo(w, midY);
    ctx.strokeStyle = "rgba(100,140,200,0.05)";
    ctx.lineWidth = 0.6;
    ctx.stroke();
    ctx.setLineDash([]);

    const timestamps = klineData.timestampsRef.current;
    if (timestamps.length === prices.length && timestamps.length > 0) {
      const tsStartIdx = startIdx;
      const visibleTimestamps = timestamps.slice(tsStartIdx);
      const timeY = h - 8;
      ctx.save();
      ctx.font = `400 9px -apple-system, BlinkMacSystemFont, "SF Pro Text", monospace`;
      ctx.textAlign = "center";
      ctx.textBaseline = "bottom";
      ctx.fillStyle = "rgba(100,140,200,0.12)";

      const labelInterval = 15;
      for (let i = 0; i < visibleTimestamps.length; i++) {
        const dataIdx = tsStartIdx + i;
        if (dataIdx % labelInterval !== 0) continue;
        const x = getX(i);
        if (x < 20 || x > dotFixedX - 30) continue;

        const ts = new Date(visibleTimestamps[i]);
        const hh = ts.getHours().toString().padStart(2, "0");
        const mm = ts.getMinutes().toString().padStart(2, "0");
        const ss = ts.getSeconds().toString().padStart(2, "0");
        const label = `${hh}:${mm}:${ss}`;

        ctx.beginPath();
        ctx.moveTo(x, h - 18);
        ctx.lineTo(x, h - 14);
        ctx.strokeStyle = "rgba(100,140,200,0.08)";
        ctx.lineWidth = 0.5;
        ctx.stroke();

        ctx.fillText(label, x, timeY);
      }

      if (visibleTimestamps.length > 0) {
        const nowTs = new Date(visibleTimestamps[visibleTimestamps.length - 1]);
        const nowHH = nowTs.getHours().toString().padStart(2, "0");
        const nowMM = nowTs.getMinutes().toString().padStart(2, "0");
        const nowSS = nowTs.getSeconds().toString().padStart(2, "0");
        ctx.fillStyle = "rgba(100,140,200,0.2)";
        ctx.fillText(`${nowHH}:${nowMM}:${nowSS}`, dotFixedX, timeY);
        ctx.beginPath();
        ctx.moveTo(dotFixedX, h - 18);
        ctx.lineTo(dotFixedX, h - 14);
        ctx.strokeStyle = "rgba(100,140,200,0.15)";
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }
      ctx.restore();
    }

    animRef.current = requestAnimationFrame(draw);
  }, [klineData]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const parent = canvas.parentElement;
      const w = parent ? parent.clientWidth : window.innerWidth;
      canvas.width = w * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${height}px`;
      const c = canvas.getContext("2d");
      if (c) c.scale(dpr, dpr);
    };

    resize();
    window.addEventListener("resize", resize);
    animRef.current = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(animRef.current);
    };
  }, [draw, height]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 pointer-events-none ${className}`}
      style={{ opacity }}
    />
  );
}
